import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronRight, type LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

interface InspectorSectionProps {
  title: string;
  icon: LucideIcon;
  count: number;
  isExpanded: boolean;
  onToggle: () => void;
  children: ReactNode;
}

export function InspectorSection({
  title,
  icon: Icon,
  count,
  isExpanded,
  onToggle,
  children,
}: InspectorSectionProps) {
  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={onToggle}
        className="text-sm font-medium flex items-center justify-between w-full text-left hover:text-primary transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-primary" />
          {title} ({count})
        </div>
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-muted-foreground" />
        ) : (
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
        )}
      </button>
      {isExpanded && <div className="space-y-1 mt-3">{children}</div>}
    </div>
  );
}

interface EmptyStateProps {
  icon: LucideIcon;
  message: string;
}

export function EmptyState({ icon: Icon, message }: EmptyStateProps) {
  return (
    <div className="flex items-center justify-center p-6 border rounded-lg bg-muted/20">
      <div className="text-center space-y-2">
        <Icon className="w-8 h-8 mx-auto text-muted-foreground/50" />
        <div className="text-sm font-medium text-muted-foreground">
          {message}
        </div>
      </div>
    </div>
  );
}

interface ToolItemProps {
  tool: {
    name: string;
    description?: string;
  };
  onNavigate: (toolName: string) => void;
}

export function ToolItem({ tool, onNavigate }: ToolItemProps) {
  return (
    <button
      onClick={() => onNavigate(tool.name)}
      type="button"
      className="flex flex-col items-start p-3 rounded text-left border hover:bg-muted/50 transition-colors cursor-pointer w-full gap-1"
    >
      <div className="font-mono text-sm font-medium truncate w-full">
        {tool.name}
      </div>
      {tool.description && (
        <div className="text-xs text-muted-foreground line-clamp-2 w-full">
          {tool.description}
        </div>
      )}
    </button>
  );
}

interface ResourceItemProps {
  resource: {
    name: string;
    uri: string;
    mimeType?: string;
  };
  onNavigate: (uri: string) => void;
}

export function ResourceItem({ resource, onNavigate }: ResourceItemProps) {
  return (
    <button
      type="button"
      onClick={() => onNavigate(resource.uri)}
      className="flex items-center justify-between p-2 rounded text-xs border hover:bg-muted/50 transition-colors cursor-pointer w-full"
    >
      <div className="flex items-center gap-2 min-w-0">
        <span className="font-mono truncate">{resource.name}</span>
      </div>
      {resource.mimeType && (
        <Badge variant="secondary" className="text-xs ml-2 flex-shrink-0">
          {resource.mimeType}
        </Badge>
      )}
    </button>
  );
}

interface PromptItemProps {
  prompt: {
    name: string;
    description?: string;
  };
  onNavigate: (promptName: string) => void;
}

export function PromptItem({ prompt, onNavigate }: PromptItemProps) {
  return (
    <button
      onClick={() => onNavigate(prompt.name)}
      type="button"
      className="flex items-center justify-between p-2 rounded text-xs border hover:bg-muted/50 transition-colors cursor-pointer w-full"
    >
      <div className="flex items-center gap-2 min-w-0">
        <span className="font-mono truncate">{prompt.name}</span>
      </div>
      {prompt.description && (
        <span className="text-xs text-muted-foreground ml-2 flex-shrink-0 truncate max-w-24">
          {prompt.description}
        </span>
      )}
    </button>
  );
}
